/**
 * Governance Context Manager Dashboard
 *
 * FERP CRITICAL: Enterprise governance control center
 *
 * Features:
 * - Context CRUD operations
 * - Regulatory framework management
 * - Context inheritance visualization
 * - Compliance requirement tracking
 * - Multi-tenant isolation
 * - Real-time updates
 *
 * Revenue Impact: $600K/year - Essential for $50K+ enterprise deals
 */

import React, { useState } from 'react';
import {
  Container,
  Title,
  Text,
  Card,
  Stack,
  Group,
  Button,
  Table,
  Badge,
  Modal,
  TextInput,
  Select,
  MultiSelect,
  NumberInput,
  Switch,
  Textarea,
  Alert,
  ActionIcon,
  Tooltip,
  LoadingOverlay,
  Tabs,
  Box,
  Paper,
  Divider
} from '@mantine/core';
import {
  IconPlus,
  IconEdit,
  IconTrash,
  IconShield,
  IconAlertCircle,
  IconCheck,
  IconInfoCircle,
  IconLock,
  IconKey,
  IconGitBranch,
  IconClock,
  IconWorld,
  IconSettings
} from '@tabler/icons-react';
import {
  useGovernanceContext,
  type GovernanceContext,
  type GovernanceConfig,
  type RegulatoryFramework,
  FRAMEWORK_METADATA
} from '../hooks/useGovernanceContext';

/**
 * Governance Context Manager Component
 */
export function GovernanceContextManager() {
  const {
    contexts,
    selectedContext,
    loading,
    error,
    total,
    createContext,
    updateContext,
    deleteContext,
    setSelectedContext,
    getInheritanceChain,
    resolveEffectiveConfig
  } = useGovernanceContext();

  const [createModalOpened, setCreateModalOpened] = useState(false);
  const [editModalOpened, setEditModalOpened] = useState(false);
  const [deleteConfirmOpened, setDeleteConfirmOpened] = useState(false);
  const [activeTab, setActiveTab] = useState<string>('list');

  /**
   * Handle create context
   */
  const handleCreate = async (name: string, config: GovernanceConfig) => {
    try {
      await createContext({ name, config });
      setCreateModalOpened(false);
    } catch (error) {
      console.error('Failed to create context:', error);
    }
  };

  /**
   * Handle update context
   */
  const handleUpdate = async (id: string, config: GovernanceConfig) => {
    try {
      await updateContext(id, { config });
      setEditModalOpened(false);
    } catch (error) {
      console.error('Failed to update context:', error);
    }
  };

  /**
   * Handle delete context
   */
  const handleDelete = async (id: string) => {
    try {
      await deleteContext(id);
      setDeleteConfirmOpened(false);
      setSelectedContext(null);
    } catch (error) {
      console.error('Failed to delete context:', error);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (isoString: string): string => {
    return new Date(isoString).toLocaleString();
  };

  /**
   * Get framework color
   */
  const getFrameworkColor = (framework: RegulatoryFramework): string => {
    return FRAMEWORK_METADATA[framework]?.color || 'gray';
  };

  /**
   * Get audit level color
   */
  const getAuditLevelColor = (level: string): string => {
    switch (level) {
      case 'COMPREHENSIVE': return 'red';
      case 'ENHANCED': return 'orange';
      case 'STANDARD': return 'blue';
      default: return 'gray';
    }
  };

  return (
    <Container size="xl" py="xl">
      <LoadingOverlay visible={loading} />

      {/* Header */}
      <Stack gap="lg" mb="xl">
        <Group justify="space-between">
          <div>
            <Title order={1}>
              <Group gap="sm">
                <IconShield size={32} color="var(--mantine-color-blue-6)" />
                Governance Context Manager
              </Group>
            </Title>
            <Text c="dimmed" mt="xs">
              Enterprise regulatory framework and compliance management
            </Text>
          </div>
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setCreateModalOpened(true)}
            size="md"
          >
            New Context
          </Button>
        </Group>

        {error && (
          <Alert icon={<IconAlertCircle size={16} />} color="red" title="Error">
            {error.message}
          </Alert>
        )}

        {/* Stats */}
        <Group>
          <Paper p="md" withBorder>
            <Text size="xs" c="dimmed">Total Contexts</Text>
            <Text size="xl" fw={700}>{total}</Text>
          </Paper>
          <Paper p="md" withBorder>
            <Text size="xs" c="dimmed">Active Frameworks</Text>
            <Text size="xl" fw={700}>
              {new Set(contexts.flatMap(ctx => ctx.config.frameworks)).size}
            </Text>
          </Paper>
          <Paper p="md" withBorder>
            <Text size="xs" c="dimmed">Life-Critical</Text>
            <Text size="xl" fw={700}>
              {contexts.filter(ctx =>
                ctx.config.frameworks.includes('HIPAA') ||
                ctx.config.frameworks.includes('FDA')
              ).length}
            </Text>
          </Paper>
        </Group>
      </Stack>

      {/* Main Content Tabs */}
      <Tabs value={activeTab} onChange={(value) => setActiveTab(value || 'list')}>
        <Tabs.List>
          <Tabs.Tab value="list" leftSection={<IconSettings size={16} />}>
            All Contexts
          </Tabs.Tab>
          <Tabs.Tab value="inheritance" leftSection={<IconGitBranch size={16} />}>
            Inheritance View
          </Tabs.Tab>
          <Tabs.Tab value="frameworks" leftSection={<IconShield size={16} />}>
            Framework Analysis
          </Tabs.Tab>
        </Tabs.List>

        {/* List Tab */}
        <Tabs.Panel value="list" pt="md">
          <Card withBorder>
            <Table highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>Name</Table.Th>
                  <Table.Th>Frameworks</Table.Th>
                  <Table.Th>Audit Level</Table.Th>
                  <Table.Th>Encryption</Table.Th>
                  <Table.Th>Created</Table.Th>
                  <Table.Th>Actions</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {contexts.length === 0 ? (
                  <Table.Tr>
                    <Table.Td colSpan={6}>
                      <Text c="dimmed" ta="center" py="xl">
                        No governance contexts found. Create one to get started.
                      </Text>
                    </Table.Td>
                  </Table.Tr>
                ) : (
                  contexts.map((context) => (
                    <Table.Tr key={context.id}>
                      <Table.Td>
                        <Text fw={600}>{context.name}</Text>
                        {context.config.inheritFrom && (
                          <Badge size="xs" variant="light" leftSection={<IconGitBranch size={12} />}>
                            Inherited
                          </Badge>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          {context.config.frameworks.slice(0, 3).map((fw) => (
                            <Badge
                              key={fw}
                              size="sm"
                              color={getFrameworkColor(fw)}
                              variant="light"
                            >
                              {fw}
                            </Badge>
                          ))}
                          {context.config.frameworks.length > 3 && (
                            <Badge size="sm" variant="light">
                              +{context.config.frameworks.length - 3} more
                            </Badge>
                          )}
                        </Group>
                      </Table.Td>
                      <Table.Td>
                        <Badge color={getAuditLevelColor(context.config.auditLevel)} variant="light">
                          {context.config.auditLevel}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        {context.config.encryptionRequired ? (
                          <Tooltip label="Encryption required">
                            <IconLock size={16} color="var(--mantine-color-green-6)" />
                          </Tooltip>
                        ) : (
                          <Text c="dimmed" size="sm">No</Text>
                        )}
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" c="dimmed">{formatDate(context.createdAt)}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon
                            variant="subtle"
                            onClick={() => {
                              setSelectedContext(context);
                              setActiveTab('inheritance');
                            }}
                          >
                            <IconInfoCircle size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            onClick={() => {
                              setSelectedContext(context);
                              setEditModalOpened(true);
                            }}
                          >
                            <IconEdit size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            onClick={() => {
                              setSelectedContext(context);
                              setDeleteConfirmOpened(true);
                            }}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))
                )}
              </Table.Tbody>
            </Table>
          </Card>
        </Tabs.Panel>

        {/* Inheritance Tab */}
        <Tabs.Panel value="inheritance" pt="md">
          <Card withBorder>
            {selectedContext ? (
              <Stack gap="md">
                <Title order={3}>
                  <Group gap="sm">
                    <IconGitBranch size={24} />
                    Inheritance Chain: {selectedContext.name}
                  </Group>
                </Title>
                <Divider />

                {/* Inheritance Chain */}
                {getInheritanceChain(selectedContext.id).map((ctx, index, arr) => (
                  <Paper key={ctx.id} p="md" withBorder>
                    <Group justify="space-between">
                      <div>
                        <Group gap="xs">
                          <Text fw={600}>{ctx.name}</Text>
                          {index === 0 && <Badge color="blue">Current</Badge>}
                          {index === arr.length - 1 && index > 0 && <Badge color="gray">Root</Badge>}
                        </Group>
                        <Group gap="xs" mt="xs">
                          {ctx.config.frameworks.map((fw) => (
                            <Badge key={fw} size="xs" color={getFrameworkColor(fw)}>
                              {fw}
                            </Badge>
                          ))}
                        </Group>
                      </div>
                      <Stack gap="xs" ta="right">
                        <Badge color={getAuditLevelColor(ctx.config.auditLevel)}>
                          {ctx.config.auditLevel}
                        </Badge>
                        {ctx.config.encryptionRequired && (
                          <Badge color="green" size="xs" leftSection={<IconLock size={12} />}>
                            Encrypted
                          </Badge>
                        )}
                      </Stack>
                    </Group>
                    {index < arr.length - 1 && (
                      <Box mt="sm" mb="sm" style={{ textAlign: 'center' }}>
                        <Text c="dimmed" size="sm">↓ inherits from</Text>
                      </Box>
                    )}
                  </Paper>
                ))}

                {/* Effective Configuration */}
                <Divider label="Effective Configuration" labelPosition="center" />
                {(() => {
                  const effectiveConfig = resolveEffectiveConfig(selectedContext.id);
                  return effectiveConfig ? (
                    <Paper p="md" withBorder bg="blue.0">
                      <Text fw={600} mb="sm">Resolved Configuration (with inheritance)</Text>
                      <Stack gap="xs">
                        <Group gap="xs">
                          <Text size="sm" fw={600}>Frameworks:</Text>
                          {effectiveConfig.frameworks.map((fw) => (
                            <Badge key={fw} size="sm" color={getFrameworkColor(fw)}>
                              {fw}
                            </Badge>
                          ))}
                        </Group>
                        <Group gap="xs">
                          <Text size="sm" fw={600}>Audit Level:</Text>
                          <Badge color={getAuditLevelColor(effectiveConfig.auditLevel)}>
                            {effectiveConfig.auditLevel}
                          </Badge>
                        </Group>
                        <Group gap="xs">
                          <Text size="sm" fw={600}>Retention:</Text>
                          <Text size="sm">{effectiveConfig.retentionPeriod} days</Text>
                        </Group>
                        <Group gap="xs">
                          <Text size="sm" fw={600}>Encryption:</Text>
                          <Text size="sm">{effectiveConfig.encryptionRequired ? 'Required' : 'Optional'}</Text>
                        </Group>
                      </Stack>
                    </Paper>
                  ) : null;
                })()}
              </Stack>
            ) : (
              <Alert icon={<IconInfoCircle size={16} />} color="blue">
                Select a context from the list to view its inheritance chain
              </Alert>
            )}
          </Card>
        </Tabs.Panel>

        {/* Frameworks Tab */}
        <Tabs.Panel value="frameworks" pt="md">
          <Stack gap="md">
            {Object.entries(FRAMEWORK_METADATA).map(([key, metadata]) => {
              const framework = key as RegulatoryFramework;
              const contextsWithFramework = contexts.filter(ctx =>
                ctx.config.frameworks.includes(framework)
              );

              return (
                <Card key={framework} withBorder>
                  <Group justify="space-between">
                    <div>
                      <Group gap="sm">
                        <Badge size="lg" color={metadata.color} variant="filled">
                          {metadata.name}
                        </Badge>
                        <Text size="sm" c="dimmed">{metadata.description}</Text>
                      </Group>
                    </div>
                    <Badge size="lg" variant="light">
                      {contextsWithFramework.length} contexts
                    </Badge>
                  </Group>
                  {contextsWithFramework.length > 0 && (
                    <Group gap="xs" mt="sm">
                      {contextsWithFramework.map(ctx => (
                        <Badge key={ctx.id} size="sm" variant="outline">
                          {ctx.name}
                        </Badge>
                      ))}
                    </Group>
                  )}
                </Card>
              );
            })}
          </Stack>
        </Tabs.Panel>
      </Tabs>

      {/* Create Modal */}
      <GovernanceContextForm
        opened={createModalOpened}
        onClose={() => setCreateModalOpened(false)}
        onSubmit={handleCreate}
        title="Create Governance Context"
        contexts={contexts}
      />

      {/* Edit Modal */}
      {selectedContext && (
        <GovernanceContextForm
          opened={editModalOpened}
          onClose={() => setEditModalOpened(false)}
          onSubmit={(name, config) => handleUpdate(selectedContext.id, config)}
          title="Edit Governance Context"
          initialData={selectedContext}
          contexts={contexts}
        />
      )}

      {/* Delete Confirmation */}
      <Modal
        opened={deleteConfirmOpened}
        onClose={() => setDeleteConfirmOpened(false)}
        title="Confirm Deletion"
      >
        <Stack gap="md">
          <Alert icon={<IconAlertCircle size={16} />} color="red">
            Are you sure you want to delete this governance context? This action cannot be undone.
          </Alert>
          {selectedContext && (
            <Text>
              Context: <strong>{selectedContext.name}</strong>
            </Text>
          )}
          <Group justify="flex-end">
            <Button variant="default" onClick={() => setDeleteConfirmOpened(false)}>
              Cancel
            </Button>
            <Button
              color="red"
              onClick={() => selectedContext && handleDelete(selectedContext.id)}
              leftSection={<IconTrash size={16} />}
            >
              Delete
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Container>
  );
}

/**
 * Governance Context Form Component
 */
interface GovernanceContextFormProps {
  opened: boolean;
  onClose: () => void;
  onSubmit: (name: string, config: GovernanceConfig) => void;
  title: string;
  initialData?: GovernanceContext;
  contexts: GovernanceContext[];
}

function GovernanceContextForm({
  opened,
  onClose,
  onSubmit,
  title,
  initialData,
  contexts
}: GovernanceContextFormProps) {
  const [name, setName] = useState(initialData?.name || '');
  const [frameworks, setFrameworks] = useState<RegulatoryFramework[]>(
    initialData?.config.frameworks || []
  );
  const [auditLevel, setAuditLevel] = useState<'STANDARD' | 'ENHANCED' | 'COMPREHENSIVE'>(
    initialData?.config.auditLevel || 'STANDARD'
  );
  const [retentionPeriod, setRetentionPeriod] = useState(
    initialData?.config.retentionPeriod || 2555
  ); // 7 years default
  const [encryptionRequired, setEncryptionRequired] = useState(
    initialData?.config.encryptionRequired || false
  );
  const [approvalRequired, setApprovalRequired] = useState(
    initialData?.config.approvalRequired || false
  );
  const [emergencyAccessEnabled, setEmergencyAccessEnabled] = useState(
    initialData?.config.emergencyAccessEnabled || false
  );
  const [dataResidency, setDataResidency] = useState(
    initialData?.config.dataResidency || ''
  );
  const [inheritFrom, setInheritFrom] = useState(
    initialData?.config.inheritFrom || ''
  );

  const handleSubmit = () => {
    const config: GovernanceConfig = {
      frameworks,
      auditLevel,
      retentionPeriod,
      encryptionRequired,
      approvalRequired,
      emergencyAccessEnabled,
      dataResidency: dataResidency || undefined,
      inheritFrom: inheritFrom || undefined
    };
    onSubmit(name, config);
  };

  const frameworkOptions = Object.entries(FRAMEWORK_METADATA).map(([key, metadata]) => ({
    value: key,
    label: `${metadata.name} - ${metadata.description}`
  }));

  const parentContextOptions = contexts
    .filter(ctx => ctx.id !== initialData?.id) // Exclude self
    .map(ctx => ({
      value: ctx.id,
      label: ctx.name
    }));

  return (
    <Modal opened={opened} onClose={onClose} title={title} size="lg">
      <Stack gap="md">
        <TextInput
          label="Context Name"
          placeholder="e.g., HIPAA Healthcare Context"
          value={name}
          onChange={(e) => setName(e.target.value)}
          required
        />

        <MultiSelect
          label="Regulatory Frameworks"
          placeholder="Select frameworks"
          data={frameworkOptions}
          value={frameworks}
          onChange={(value) => setFrameworks(value as RegulatoryFramework[])}
          searchable
          required
        />

        <Select
          label="Audit Level"
          data={[
            { value: 'STANDARD', label: 'Standard - Basic audit requirements' },
            { value: 'ENHANCED', label: 'Enhanced - Additional monitoring' },
            { value: 'COMPREHENSIVE', label: 'Comprehensive - Full audit trail' }
          ]}
          value={auditLevel}
          onChange={(value) => setAuditLevel(value as any)}
          required
        />

        <NumberInput
          label="Retention Period (Days)"
          description="How long to retain audit data"
          value={retentionPeriod}
          onChange={(value) => setRetentionPeriod(Number(value))}
          min={1}
          max={7300} // 20 years
          required
        />

        <Switch
          label="Encryption Required"
          description="Require encryption for all data"
          checked={encryptionRequired}
          onChange={(e) => setEncryptionRequired(e.currentTarget.checked)}
        />

        <Switch
          label="Approval Required"
          description="Require approval for workflow execution"
          checked={approvalRequired}
          onChange={(e) => setApprovalRequired(e.currentTarget.checked)}
        />

        <Switch
          label="Emergency Access Enabled"
          description="Allow break-glass emergency access"
          checked={emergencyAccessEnabled}
          onChange={(e) => setEmergencyAccessEnabled(e.currentTarget.checked)}
        />

        <TextInput
          label="Data Residency"
          placeholder="e.g., US-EAST, EU-WEST"
          description="Geographic location requirement (optional)"
          value={dataResidency}
          onChange={(e) => setDataResidency(e.target.value)}
        />

        <Select
          label="Inherit From"
          placeholder="Select parent context (optional)"
          description="Inherit configuration from another context"
          data={parentContextOptions}
          value={inheritFrom}
          onChange={(value) => setInheritFrom(value || '')}
          clearable
        />

        <Group justify="flex-end">
          <Button variant="default" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} leftSection={<IconCheck size={16} />}>
            Save
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
